import React, { useState } from 'react';
import { 
  Calendar, 
  Plus, 
  Clock, 
  MapPin, 
  Users,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Eye,
  RefreshCw,
  Download,
  MessageSquare,
  CalendarX,
  X,
  Send,
  Link,
  ExternalLink,
  Mail,
  FileText
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';

// Removed unused component definitions - functionality now inline in main modal

import { Avatar, AvatarImage, AvatarFallback } from '../../ui/avatar';

const TrainingSchedule = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // month, week, day
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  // Removed unused state variables - functionality now inline in main modal

  // Mock training events
  const trainingEvents = [
    {
      id: 1,
      title: 'Executive Leadership Workshop',
      trainer: 'Dr. Sarah Johnson',
      startDate: '2025-11-05T09:00:00',
      endDate: '2025-11-05T17:00:00',
      location: 'Conference Room A',
      participants: 25,
      status: 'confirmed',
      type: 'workshop',
      description: 'Comprehensive leadership development program for senior management',
      contact: 'Dr. Sarah Johnson',
      date: 'November 5, 2025',
      time: '9:00 AM - 5:00 PM',
      notes: 'Please bring laptops and notebooks',
      materials: ['Leadership handbook', 'Case study materials', 'Assessment forms']
    },
    {
      id: 2,
      title: 'Digital Transformation Training',
      trainer: 'Michael Chen',
      startDate: '2025-11-08T10:00:00',
      endDate: '2025-11-08T16:00:00',
      location: 'Virtual Session',
      participants: 40,
      status: 'confirmed',
      type: 'training',
      description: 'Digital transformation strategies for IT teams',
      contact: 'Michael Chen',
      date: 'November 8, 2025',
      time: '10:00 AM - 4:00 PM',
      notes: 'Online session - Zoom link will be provided',
      materials: ['Digital transformation guide', 'Software tools overview', 'Best practices checklist']
    },
    {
      id: 3,
      title: 'Project Management Fundamentals',
      trainer: 'Emily Rodriguez',
      startDate: '2025-11-12T09:30:00',
      endDate: '2025-11-12T15:30:00',
      location: 'Training Center B',
      participants: 15,
      status: 'pending',
      type: 'course',
      description: 'Foundation course in project management methodologies',
      contact: 'Emily Rodriguez',
      date: 'November 12, 2025',
      time: '9:30 AM - 3:30 PM',
      notes: 'Small group interactive session',
      materials: ['PM fundamentals workbook', 'Project templates', 'Certification info']
    },
    {
      id: 4,
      title: 'Team Building Retreat',
      trainer: 'James Wilson',
      startDate: '2025-11-15T08:00:00',
      endDate: '2025-11-15T18:00:00',
      location: 'Outdoor Activity Center',
      participants: 20,
      status: 'confirmed',
      type: 'retreat',
      description: 'Full-day team building activities and exercises',
      contact: 'James Wilson',
      date: 'November 15, 2025',
      time: '8:00 AM - 6:00 PM',
      notes: 'Weather dependent - backup indoor venue available',
      materials: ['Activity gear provided', 'Comfortable clothing recommended', 'Lunch included']
    },
    {
      id: 5,
      title: 'Change Management Workshop',
      trainer: 'Lisa Zhang',
      startDate: '2025-11-20T09:00:00',
      endDate: '2025-11-22T17:00:00',
      location: 'Main Auditorium',
      participants: 35,
      status: 'draft',
      type: 'workshop',
      description: 'Three-day intensive change management program',
      contact: 'Lisa Zhang',
      date: 'November 20-22, 2025',
      time: '9:00 AM - 5:00 PM daily',
      notes: 'Multi-day intensive program',
      materials: ['Change management toolkit', 'Case studies', 'Action plan templates']
    },
    {
      id: 6,
      title: 'Technical Skills Bootcamp',
      trainer: 'Michael Chen',
      startDate: '2025-11-25T09:00:00',
      endDate: '2025-11-27T17:00:00',
      location: 'Tech Lab',
      participants: 30,
      status: 'confirmed',
      type: 'bootcamp',
      description: 'Intensive 3-day technical skills development program',
      contact: 'Michael Chen',
      date: 'November 25-27, 2025',
      time: '9:00 AM - 5:00 PM daily',
      notes: 'Bring laptops and chargers',
      materials: ['Technical manuals', 'Software licenses', 'Project assignments']
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800 border-green-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'draft': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'workshop': return 'bg-blue-500';
      case 'training': return 'bg-green-500';
      case 'course': return 'bg-purple-500';
      case 'retreat': return 'bg-orange-500';
      case 'bootcamp': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  // Calendar generation functions
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isToday = (date) => {
    const today = new Date();
    const compareDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), date);
    return compareDate.toDateString() === today.toDateString();
  };

  const getEventsForDate = (date) => {
    const targetDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), date);
    return trainingEvents.filter(event => {
      const eventDate = new Date(event.startDate);
      return eventDate.toDateString() === targetDate.toDateString();
    });
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  // Calendar sync functions
  const syncWithGoogleCalendar = async () => {
    setIsSyncing(true);
    try {
      // Simulate Google Calendar sync
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // In a real implementation, this would:
      // 1. Authenticate with Google Calendar API
      // 2. Create/update events in Google Calendar
      // 3. Handle OAuth flow
      
      alert('✅ Successfully synced with Google Calendar!\n\nAll training events have been added to your Google Calendar.');
    } catch (error) {
      alert('❌ Failed to sync with Google Calendar. Please try again.');
    } finally {
      setIsSyncing(false);
    }
  };

  const syncWithOutlook = async () => {
    setIsSyncing(true);
    try {
      // Simulate Outlook sync
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // In a real implementation, this would:
      // 1. Authenticate with Microsoft Graph API
      // 2. Create/update events in Outlook Calendar
      // 3. Handle OAuth flow
      
      alert('✅ Successfully synced with Outlook Calendar!\n\nAll training events have been added to your Outlook Calendar.');
    } catch (error) {
      alert('❌ Failed to sync with Outlook Calendar. Please try again.');
    } finally {
      setIsSyncing(false);
    }
  };

  const exportToICS = () => {
    // Generate ICS file content
    let icsContent = 'BEGIN:VCALENDAR\nVERSION:2.0\nPRODID:-//TrainerLinked.in//Training Schedule//EN\n';
    
    trainingEvents.forEach(event => {
      const startDate = new Date(event.startDate).toISOString().replace(/[:-]/g, '').split('.')[0] + 'Z';
      const endDate = new Date(event.endDate).toISOString().replace(/[:-]/g, '').split('.')[0] + 'Z';
      
      icsContent += `BEGIN:VEVENT\n`;
      icsContent += `UID:${event.id}@trainerlinkedin.com\n`;
      icsContent += `DTSTART:${startDate}\n`;
      icsContent += `DTEND:${endDate}\n`;
      icsContent += `SUMMARY:${event.title}\n`;
      icsContent += `DESCRIPTION:${event.description}\n`;
      icsContent += `LOCATION:${event.location}\n`;
      icsContent += `END:VEVENT\n`;
    });
    
    icsContent += 'END:VCALENDAR';
    
    // Create and download ICS file
    const blob = new Blob([icsContent], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'training-schedule.ics';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Event action handlers
  // Removed unused handler functions - functionality now inline in buttons

  // Removed unused functions - functionality now inline in main modal

  const renderCalendarGrid = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];
    const today = new Date();

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-32 border border-gray-200"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dayEvents = getEventsForDate(day);
      const isCurrentDay = isToday(day);

      days.push(
        <div 
          key={day} 
          className={`h-32 border border-gray-200 p-1 ${isCurrentDay ? 'bg-blue-50' : 'hover:bg-gray-50'} cursor-pointer`}
          onDoubleClick={() => {
            if (dayEvents.length > 0) {
              // If there are multiple events, show the first one or a selection modal
              if (dayEvents.length === 1) {
                setSelectedEvent(dayEvents[0]);
              } else {
                // For multiple events, show the first one (you could create a selection modal instead)
                setSelectedEvent(dayEvents[0]);
              }
            }
          }}
          title={dayEvents.length > 0 ? `Double-click to view details for ${dayEvents.length} event(s)` : ''}
        >
          <div className={`text-sm font-medium mb-1 ${isCurrentDay ? 'text-blue-600' : 'text-gray-900'}`}>
            {day}
          </div>
          <div className="space-y-1">
            {dayEvents.slice(0, 2).map(event => (
              <div
                key={event.id}
                onClick={() => setSelectedEvent(event)}
                className={`text-xs p-1 rounded cursor-pointer truncate ${getStatusColor(event.status)} hover:opacity-80`}
              >
                <div className="flex items-center space-x-1">
                  <div className={`w-2 h-2 rounded-full ${getTypeColor(event.type)}`}></div>
                  <span className="truncate">{event.title}</span>
                </div>
              </div>
            ))}
            {dayEvents.length > 2 && (
              <div 
                className="text-xs text-gray-600 cursor-pointer hover:text-gray-800"
                onClick={(e) => {
                  e.stopPropagation();
                  // Show all events for this day
                  setSelectedEvent(dayEvents[2]); // or create a multi-event modal
                }}
              >
                +{dayEvents.length - 2} more
              </div>
            )}
          </div>
        </div>
      );
    }

    return days;
  };

  const renderUpcomingEvents = () => {
    const upcomingEvents = trainingEvents
      .filter(event => new Date(event.startDate) >= new Date())
      .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
      .slice(0, 5);

    return upcomingEvents.map(event => (
      <div key={event.id} className="flex items-start space-x-3 p-3 border rounded-lg hover:shadow-sm transition-shadow">
        <div className={`w-3 h-3 rounded-full mt-2 ${getTypeColor(event.type)}`}></div>
        <div className="flex-1">
          <h4 className="font-medium text-gray-900">{event.title}</h4>
          <p className="text-sm text-gray-600">{event.trainer}</p>
          <div className="flex items-center space-x-4 text-xs text-gray-500 mt-1">
            <div className="flex items-center">
              <Calendar className="w-3 h-3 mr-1" />
              {formatDate(event.startDate)}
            </div>
            <div className="flex items-center">
              <Clock className="w-3 h-3 mr-1" />
              {formatTime(event.startDate)} - {formatTime(event.endDate)}
            </div>
            <div className="flex items-center">
              <MapPin className="w-3 h-3 mr-1" />
              {event.location}
            </div>
          </div>
        </div>
        <Badge className={getStatusColor(event.status)}>
          {event.status}
        </Badge>
      </div>
    ));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Calendar className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Training Schedule</h1>
            <p className="text-gray-600">Manage and view your training calendar</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {/* Calendar Sync Dropdown */}
          <div className="relative group">
            <Button variant="outline" className="flex items-center space-x-2" disabled={isSyncing}>
              <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
              <span>Sync Calendar</span>
            </Button>
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
              <div className="py-1">
                <button
                  onClick={syncWithGoogleCalendar}
                  disabled={isSyncing}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                >
                  🟢 Sync with Google Calendar
                </button>
                <button
                  onClick={syncWithOutlook}
                  disabled={isSyncing}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                >
                  🔵 Sync with Outlook
                </button>
                <button
                  onClick={exportToICS}
                  disabled={isSyncing}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                >
                  📥 Export as ICS file
                </button>
              </div>
            </div>
          </div>
          
          <Button className="bg-purple-600 hover:bg-purple-700">
            <Plus className="w-4 h-4 mr-2" />
            Schedule Training
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Calendar View */}
        <div className="col-span-8">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <h2 className="text-xl font-semibold">
                    {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </h2>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigateMonth(-1)}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentDate(new Date())}
                    >
                      Today
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigateMonth(1)}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <select
                    value={viewMode}
                    onChange={(e) => setViewMode(e.target.value)}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="month">Month</option>
                    <option value="week">Week</option>
                    <option value="day">Day</option>
                  </select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Calendar Header */}
              <div className="grid grid-cols-7 gap-0 mb-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="p-2 text-center text-sm font-medium text-gray-600 border-b">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-0">
                {renderCalendarGrid()}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="col-span-4 space-y-6">
          {/* Upcoming Events */}
          <Card>
            <CardHeader>
              <CardTitle>Upcoming Trainings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {renderUpcomingEvents()}
            </CardContent>
          </Card>

          {/* Event Legend */}
          <Card>
            <CardHeader>
              <CardTitle>Event Types</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {[
                  { type: 'workshop', label: 'Workshops' },
                  { type: 'training', label: 'Training Sessions' },
                  { type: 'course', label: 'Courses' },
                  { type: 'retreat', label: 'Retreats' },
                  { type: 'bootcamp', label: 'Bootcamps' }
                ].map(item => (
                  <div key={item.type} className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full ${getTypeColor(item.type)}`}></div>
                    <span className="text-sm text-gray-700">{item.label}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card>
            <CardHeader>
              <CardTitle>This Month</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Total Sessions</span>
                  <span className="font-semibold">{trainingEvents.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Confirmed</span>
                  <span className="font-semibold text-green-600">
                    {trainingEvents.filter(e => e.status === 'confirmed').length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Pending</span>
                  <span className="font-semibold text-yellow-600">
                    {trainingEvents.filter(e => e.status === 'pending').length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Total Participants</span>
                  <span className="font-semibold">
                    {trainingEvents.reduce((sum, e) => sum + e.participants, 0)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* WORKING Event Details Modal */}
      {selectedEvent && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 999999,
          padding: '16px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            padding: '24px'
          }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', borderBottom: '1px solid #e5e7eb', paddingBottom: '16px' }}>
              <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827' }}>{selectedEvent.title}</h2>
              <button 
                onClick={() => setSelectedEvent(null)}
                style={{ 
                  background: 'none', 
                  border: '1px solid #d1d5db', 
                  borderRadius: '6px', 
                  padding: '8px', 
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <X style={{ width: '16px', height: '16px' }} />
              </button>
            </div>

            {/* Training Details */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px' }}>Training Information</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <strong>Trainer:</strong> {selectedEvent.trainer}<br/>
                  <strong>Contact:</strong> {selectedEvent.contact}<br/>
                  <strong>Participants:</strong> {selectedEvent.participants}
                </div>
                <div>
                  <strong>Date:</strong> {selectedEvent.date}<br/>
                  <strong>Time:</strong> {selectedEvent.time}<br/>
                  <strong>Location:</strong> {selectedEvent.location}
                </div>
              </div>
              <div style={{ marginTop: '16px' }}>
                <strong>Description:</strong><br/>
                <p style={{ marginTop: '8px', color: '#6b7280' }}>{selectedEvent.description}</p>
              </div>
            </div>

            {/* Training Materials */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px' }}>Training Materials (Workbooks & Workshop Slides)</h3>
              {selectedEvent.materials && selectedEvent.materials.map((material, index) => (
                <div key={index} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  padding: '12px', 
                  border: '1px solid #e5e7eb', 
                  borderRadius: '6px', 
                  marginBottom: '8px',
                  backgroundColor: '#f9fafb'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FileText style={{ width: '16px', height: '16px', marginRight: '8px', color: '#6b7280' }} />
                    <span>{material}</span>
                  </div>
                  <button
                    onClick={() => {
                      const linkType = prompt('Select link type:\n1. Cloud Storage (Google Drive, Dropbox, OneDrive)\n2. Local Computer Folder\n3. Custom URL\n\nEnter 1, 2, or 3:');
                      
                      let url = '';
                      if (linkType === '1') {
                        url = prompt(`Enter cloud storage link for ${material}:\n(e.g., https://drive.google.com/drive/folders/...)`);
                      } else if (linkType === '2') {
                        url = prompt(`Enter local folder path for ${material}:\n(e.g., C:\\Training Materials\\${material}\\ or /Users/trainer/materials/${material}/)`);
                      } else if (linkType === '3') {
                        url = prompt(`Enter custom URL for ${material}:`);
                      }
                      
                      if (url) {
                        alert(`✅ Link saved for ${material}!\n\nType: ${linkType === '1' ? 'Cloud Storage' : linkType === '2' ? 'Local Folder' : 'Custom URL'}\nLink: ${url}\n\nParticipants can now access this material.`);
                      }
                    }}
                    style={{
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '6px 12px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <Link style={{ width: '12px', height: '12px' }} />
                    Add Link
                  </button>
                </div>
              ))}
              
              {/* Send Materials in Advance */}
              <div style={{ 
                marginTop: '16px', 
                padding: '16px', 
                backgroundColor: '#dbeafe', 
                borderRadius: '6px',
                border: '1px solid #93c5fd'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Mail style={{ width: '16px', height: '16px', color: '#2563eb', marginRight: '8px', marginTop: '2px' }} />
                  <div>
                    <p style={{ fontWeight: '600', color: '#1e40af', marginBottom: '4px' }}>Send Materials in Advance?</p>
                    <p style={{ fontSize: '12px', color: '#2563eb', marginBottom: '12px' }}>
                      Send these materials to participants before the training session.
                    </p>
                    <button
                      onClick={() => {
                        const email = `Dear ${selectedEvent.contact || 'Training Manager'},

I hope this email finds you well. I am writing to share the training materials for our upcoming session:

Training Program: ${selectedEvent.title}
Date & Time: ${selectedEvent.date} at ${selectedEvent.time}
Location: ${selectedEvent.location}
Participants: ${selectedEvent.participants}

Please find the training materials that should be shared with participants 2-3 days before the session:

${selectedEvent.materials?.map(material => `• ${material}`).join('\n') || '• Materials list to be provided'}

These materials will help participants:
- Understand the session objectives
- Come prepared with relevant questions  
- Maximize their learning experience

Please let me know if you need any clarification or have questions about the materials.

Looking forward to delivering an excellent training experience!

Best regards,
[Your Name]
[Your Contact Information]`;
                        
                        const subject = `Training Materials for ${selectedEvent.title} - ${selectedEvent.date}`;
                        const mailto = `mailto:${selectedEvent.contact || 'training.manager@company.com'}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(email)}`;
                        
                        // Open email client
                        window.location.href = mailto;
                        
                        // Show confirmation
                        setTimeout(() => {
                          alert('✅ Email compose window opened!\n\nThe email has been pre-filled with training details and materials. Please review and send from your email client.');
                        }, 500);
                      }}
                      style={{
                        backgroundColor: '#2563eb',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '8px 16px',
                        fontSize: '14px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <Mail style={{ width: '14px', height: '14px' }} />
                      Compose Email to HR/Training Manager
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => {
                    alert(`📋 COMPLETE TRAINING DETAILS

📚 Program: ${selectedEvent.title}
👨‍🏫 Trainer: ${selectedEvent.trainer}
📅 Date: ${selectedEvent.date}
🕐 Time: ${selectedEvent.time}  
📍 Location: ${selectedEvent.location}
👥 Participants: ${selectedEvent.participants}
📞 Contact: ${selectedEvent.contact}

📝 Description:
${selectedEvent.description}

📋 Notes: ${selectedEvent.notes || 'No additional notes'}

📚 Materials:
${selectedEvent.materials?.map(material => `• ${material}`).join('\n') || 'No materials listed'}`);
                  }}
                  style={{
                    backgroundColor: 'white',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <Eye style={{ width: '14px', height: '14px' }} />
                  View Complete Details
                </button>
                
                <button
                  onClick={() => {
                    const subject = `Regarding ${selectedEvent.title} Training Session`;
                    const body = `Dear ${selectedEvent.contact || 'Training Manager'},

I hope this message finds you well. I am reaching out regarding the upcoming training session:

Training Program: ${selectedEvent.title}
Date & Time: ${selectedEvent.date} at ${selectedEvent.time}
Location: ${selectedEvent.location}  
Participants: ${selectedEvent.participants}

[Please add your message here]

If you have any questions or need to discuss any aspects of the training, please feel free to reach out.

Best regards,
[Your Name]
[Your Contact Information]`;
                    
                    const mailto = `mailto:${selectedEvent.contact || 'training.manager@company.com'}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                    
                    // Open email client
                    window.location.href = mailto;
                    
                    // Show confirmation
                    setTimeout(() => {
                      alert('✅ Email compose window opened!\n\nA general message template has been prepared. Please customize and send from your email client.');
                    }, 500);
                  }}
                  style={{
                    backgroundColor: 'white',
                    color: '#374151', 
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <MessageSquare style={{ width: '14px', height: '14px' }} />
                  Send Message to HR
                </button>
              </div>
              
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  style={{
                    backgroundColor: 'white',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <Edit style={{ width: '14px', height: '14px' }} />
                  Edit
                </button>
                
                <button
                  onClick={() => {
                    if (confirm(`⚠️ Are you sure you want to cancel "${selectedEvent.title}"?\n\nThis action cannot be undone. Participants will be notified of the cancellation.`)) {
                      alert(`✅ Training session "${selectedEvent.title}" has been cancelled.\n\n📧 Participants and stakeholders will be notified automatically.\n📅 Calendar entries will be updated.`);
                      setSelectedEvent(null);
                    }
                  }}
                  style={{
                    backgroundColor: 'white',
                    color: '#dc2626',
                    border: '1px solid #fca5a5',
                    borderRadius: '4px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <Trash2 style={{ width: '14px', height: '14px' }} />
                  Cancel Session
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Removed - Detailed view functionality now in main modal */}

      {/* Send Message Modal - Removed, functionality now in main modal */}

      {/* Removed - All functionality now in main modal with inline handlers */}
    </div>
  );
};

export default TrainingSchedule;